// ============================================================================
// Shared helpers for the practice games (presentation layer). Content lookups,
// answer normalization, and multiple-choice option building. Kept out of the
// portable core because option-building uses randomness.
// ============================================================================

import { ITEM_TYPE, SKILL } from '@core/models/constants.js';

/** Primary "hun eum" reading string for a character row (with readings joined). */
export function primaryReading(charRow) {
  const rs = charRow?.character_readings || [];
  const p = rs.find((r) => r.is_primary) || rs[0];
  return p ? `${p.hun ?? ''} ${p.eum ?? ''}`.trim() : '';
}
export function primaryEum(charRow) {
  const rs = charRow?.character_readings || [];
  const p = rs.find((r) => r.is_primary) || rs[0];
  return p?.eum ?? '';
}

/** Normalize a typed answer for comparison (NFC, trimmed, spaces collapsed). */
export function normalize(s) {
  return (s ?? '')
    .normalize('NFC')
    .trim()
    .replace(/\s+/g, '')
    .toLowerCase();
}

export function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Build up to `n` unique multiple-choice options including the correct one.
 * `pool` is an array of candidate strings; blanks/dupes are filtered.
 */
export function buildChoices(correct, pool, n = 4) {
  const seen = new Set([correct]);
  const distractors = [];
  for (const cand of shuffle(pool)) {
    const c = (cand ?? '').trim();
    if (!c || seen.has(c)) continue;
    seen.add(c);
    distractors.push(c);
    if (distractors.length >= n - 1) break;
  }
  return shuffle([correct, ...distractors]);
}

/** The audio string to speak for an item (character eum or word hangul). */
export function speakText(item, content) {
  if (item.item_type === ITEM_TYPE.CHARACTER) return primaryEum(content) || content?.char || '';
  return content?.hangul || '';
}

export { ITEM_TYPE, SKILL };
